import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Box,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { WishlistItem } from '../../hooks/useWishlist';
import { formatCentavosBR, centavosToNumber, numberToCentavos } from '../../utils/priceParser';
import { useDialogKeyboardScroll } from '../../hooks/useDialogKeyboardScroll';

interface EditWishlistItemDialogProps {
  open: boolean;
  item: WishlistItem | null;
  onClose: () => void;
  onSave: (itemId: string, updates: { title: string; notes?: string; store?: string; price?: number; purchased?: boolean; purchasedAt?: Date; url?: string; imageUrl?: string }) => Promise<void>;
}

export const EditWishlistItemDialog: React.FC<EditWishlistItemDialogProps> = ({
  open,
  item,
  onClose,
  onSave,
}) => {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [notes, setNotes] = useState('');
  const [store, setStore] = useState('');
  const [priceCents, setPriceCents] = useState(''); // string de dígitos (centavos)
  const [purchased, setPurchased] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useDialogKeyboardScroll(open);

  useEffect(() => {
    if (item) {
      setTitle(item.title || '');
      setUrl(item.url || '');
      setImageUrl(item.imageUrl || '');
      setNotes(item.notes || '');
      setStore(item.store || '');
      setPriceCents(numberToCentavos(item.price));
      setPurchased(item.purchased || false);
    }
  }, [item]);

  const handleSubmit = async () => {
    if (!item || !title.trim()) return;

    try {
      setSaving(true);
      setError(null);

      const updates: any = {
        title: title.trim(),
        purchased,
      };

      if (purchased) updates.purchasedAt = new Date();
      if (notes.trim()) updates.notes = notes.trim();
      if (store.trim()) updates.store = store.trim();
      if (url.trim()) updates.url = url.trim();
      if (imageUrl.trim()) updates.imageUrl = imageUrl.trim();

      const parsedPrice = centavosToNumber(priceCents);
      if (parsedPrice !== null) updates.price = parsedPrice;

      await onSave(item.id, updates);

      setTitle('');
      setUrl('');
      setImageUrl('');
      setNotes('');
      setStore('');
      setPriceCents('');
      setPurchased(false);
      onClose();
    } catch {
      setError('Erro ao atualizar item');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
      sx={{
        '& .MuiDialog-paper': {
          maxHeight: 'calc(100dvh - env(safe-area-inset-top) - env(safe-area-inset-bottom) - 32px)',
          marginTop: 'env(safe-area-inset-top)',
          marginBottom: 'env(safe-area-inset-bottom)',
        },
      }}
    >
      <DialogTitle>Editar Item</DialogTitle>
      <DialogContent sx={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <TextField
          fullWidth
          label="Título"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          margin="normal"
          error={!!error}
          helperText={error}
          autoFocus
        />
        <TextField
          fullWidth
          label="URL do produto (opcional)"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          margin="normal"
          placeholder="https://..."
          slotProps={{ htmlInput: { autoCapitalize: 'none', autoCorrect: 'off' } }}
        />
        <TextField
          fullWidth
          label="URL da imagem (opcional)"
          value={imageUrl}
          onChange={(e) => setImageUrl(e.target.value)}
          margin="normal"
          placeholder="https://..."
          slotProps={{ htmlInput: { autoCapitalize: 'none', autoCorrect: 'off' } }}
        />
        <TextField
          fullWidth
          label="Nome da loja (opcional)"
          value={store}
          onChange={(e) => setStore(e.target.value)}
          margin="normal"
          placeholder="Ex: Amazon, Camicado, etc."
        />
        <TextField
          fullWidth
          label="Valor (opcional)"
          value={formatCentavosBR(priceCents)}
          onChange={(e) => setPriceCents(e.target.value.replace(/\D/g, ''))}
          margin="normal"
          placeholder="0,00"
          slotProps={{
            input: {
              startAdornment: <Box component="span" sx={{ mr: 0.5 }}>R$</Box>,
              inputMode: 'numeric',
            },
            htmlInput: { inputMode: 'numeric', pattern: '[0-9]*' },
          }}
        />
        <TextField
          fullWidth
          label="Notas (opcional)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          margin="normal"
          multiline
          rows={3}
          placeholder="Adicione observações sobre este item..."
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={purchased}
              onChange={(e) => setPurchased(e.target.checked)}
            />
          }
          label="Marcar como comprado"
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button 
          onClick={handleSubmit}
          disabled={!title.trim() || saving}
          variant="contained"
        >
          {saving ? <CircularProgress size={20} color="inherit" /> : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
